import {EMPHASIS} from '@forecast-it/design-system';
import moment, {type Moment} from 'moment';
import React, {useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {getCompanyLockedDate, getLockedDate} from '../../../components/timesheets/time-lock/TimeLockUtil';
import {
	ApprovalStatus,
	BUTTON_COLOR,
	BUTTON_STYLE,
	DATE_FORMAT_DAY,
	ELEMENT_TYPE,
	ElementType,
	GLOBAL_FILTER_FIELD,
	GLOBAL_FILTER_OPERATOR,
} from '../../../constants';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';
import {PERMISSION_TYPE} from '../../../Permissions';
import * as tracking from '../../../tracking';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import HeaderBar from '../../shared/components/headers/header-bar/header_bar';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import {isBillableSplitAllowed} from '../../shared/util/cache/TimeRegistrationSettingsUtil';
import {
	usingTimeApproval as isUsingTimeApproval,
	usingTimeOffApproval as isUsingTimeOffApproval,
} from '../../shared/util/FeatureUtil';
import {hasPermission} from '../../shared/util/PermissionsUtil';
import Util from '../../shared/util/util';
import {useStoredState} from '@/forecast-app/shared/hooks/useStoredState';
import {TIMESHEET_OPTION_STORAGE_KEY} from './bottom-section/timesheets-time-registration-table/useTimesheetColumnEyeOptions';
import {TIME_PERIOD, TimePeriod} from './timesheets_change_date_view';

interface TimesheetPageViewer {
	actualPersonId: number;
	company: {
		person: {
			id: number;
			fullName: string;
		};
		useTimeApproval: boolean;
		useTimeOffApproval: boolean;
	};
}

interface TimesheetHeaderProps {
	viewer: TimesheetPageViewer;
	showCalendar: boolean;
	setShowCalendar: React.Dispatch<React.SetStateAction<boolean>>;
	selectedPersonId: number;
	setSelectedPersonId: React.Dispatch<React.SetStateAction<number>>;
	timePeriod: TimePeriod;
	setTimePeriod: React.Dispatch<React.SetStateAction<string>>;
	currentViewingDate: Moment;
	setCurrentViewingDate: React.Dispatch<React.SetStateAction<Moment>>;
	currentViewingMonth: Moment;
	setCurrentViewingMonth: React.Dispatch<React.SetStateAction<Moment>>;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	searchString: string;
	setSearchString: React.Dispatch<React.SetStateAction<string>>;
	showAddTimeRegButton: boolean;
	isFullyLocked: boolean;
	lastWorkingDayInWeek: boolean;
	periodTimeApprovalStatus?: ApprovalStatus;
	hasProjectTimeRegsInWeek?: boolean;
}

interface HeaderContent {
	type: ElementType;
}

export const TimesheetHeader = ({
	selectedPersonId,
	setSelectedPersonId,
	currentViewingMonth,
	showCalendar,
	currentViewingDate,
	timePeriod,
	setCurrentViewingDate,
	setCurrentViewingMonth,
	setShowCalendar,
	setTimePeriod,
	searchString,
	setSearchString,
	showAddTimeRegButton,
	isFullyLocked,
	lastWorkingDayInWeek,
	viewer,
	periodTimeApprovalStatus,
	hasProjectTimeRegsInWeek = false,
}: TimesheetHeaderProps): JSX.Element => {
	const {formatMessage, formatDate} = useIntl();
	const [timesheetEyeOptions] = useStoredState<Record<string, boolean>>(TIMESHEET_OPTION_STORAGE_KEY, {});
	const {company, actualPersonId} = viewer;
	const {person: selectedPerson, useTimeApproval, useTimeOffApproval} = company;
	const handlePersonChange = person => {
		tracking.trackPageAction('Person Changed');
		trackEvent('Person', 'Changed', {
			selectedPerson: person.value ? (person.value === actualPersonId ? 'myself' : person.email) : 'myself',
		});
		dispatch(EVENT_ID.TIMESHEET_PERSON_CHANGED, person.value);
		setSelectedPersonId(person.value);
	};

	//Next/Previous date range buttons
	const adjacentPeriod = forward => {
		const period = showCalendar ? 'month' : timePeriod;

		//if the day selected is a day outside of the month selected we don't want to jump to the next/previous month but to the month oof the day selected
		const keepMonth = showCalendar && currentViewingDate.month() !== currentViewingMonth.month();
		const diff = keepMonth ? 0 : forward ? 1 : -1;
		const newDate = currentViewingDate.clone().add(diff, period);
		setCurrentViewingDate(newDate);
		setCurrentViewingMonth(newDate);
	};

	//Date picker
	const handleDateRange = (date: Moment) => {
		setCurrentViewingDate(date);
		setCurrentViewingMonth(date);
	};

	const toggleCalendar = () => {
		setShowCalendar(!showCalendar);
		trackEvent('Calender Toggle', 'Clicked', {showCalender: !showCalendar});
	};

	//Today Button
	const goToToday = () => {
		setCurrentViewingDate(moment());
		setCurrentViewingMonth(moment());
	};

	// The CVS export date - taken from the current selected month
	const csvStartDate = moment([currentViewingMonth.year(), currentViewingMonth.month()]);
	const csvEndDate = currentViewingMonth.clone().endOf('month');

	// The CVS search query values
	const csvSearchQuery = {
		filters: [
			{
				field: GLOBAL_FILTER_FIELD.WITH_TIME_REG,
				operator: GLOBAL_FILTER_OPERATOR.IS,
				value: true.toString(),
			},
			{
				field: GLOBAL_FILTER_FIELD.START_DATE_TIME_REG,
				operator: GLOBAL_FILTER_OPERATOR.GREATER_OR_EQUAL,
				value: csvStartDate.format(DATE_FORMAT_DAY),
			},
			{
				field: GLOBAL_FILTER_FIELD.END_DATE_TIME_REG,
				operator: GLOBAL_FILTER_OPERATOR.LESS_OR_EQUAL,
				value: csvEndDate.format(DATE_FORMAT_DAY),
			},
			{
				field: GLOBAL_FILTER_FIELD.PERSON_TIME_REG,
				operator: GLOBAL_FILTER_OPERATOR.IS,
				value: [selectedPersonId],
			},
		],
	};

	const handleCsvDownload = () => {
		trackEvent('Download CSV Button', 'Clicked');
		showModal({
			type: MODAL_TYPE.REPORTED_TIME_DATE_CSV,
			searchQuery: csvSearchQuery,
			enabledColumns: {
				project: true,
				date: true,
				reportedTime: true,
				billableTime: isBillableSplitAllowed(),
				task: true,
				notes: true,
				phase: timesheetEyeOptions['phase'] ?? true,
				client: timesheetEyeOptions['client'] ?? false,
				remaining: timesheetEyeOptions['remaining'] ?? false,
			},
			legacyDownload: true,
		});
	};

	const hasTimeRegistrationCreateAllPermission = hasPermission(PERMISSION_TYPE.TIME_REGISTRATION_CREATE_ALL);

	useEffect(() => {
		const dateFromURL = Util.getUrlQueryParameter('date');
		if (dateFromURL) {
			const splitDate = dateFromURL.split('-');
			if (splitDate.length === 3) {
				const date = dateFromURL ? Util.CreateNonUtcMomentDate(splitDate[0], splitDate[1], splitDate[2]) : null;
				const personIdFromURL = Util.getUrlQueryParameter('personId');
				if (date && personIdFromURL) {
					setTimePeriod(TIME_PERIOD.WEEK_VIEW);
					if (hasTimeRegistrationCreateAllPermission) {
						setSelectedPersonId(personIdFromURL);
						handleDateRange(date);
					} else {
						handleDateRange(date);
					}
				}
			}
		}
	}, []);

	const leftContent: HeaderContent[] = [];
	const rightContent: HeaderContent[] = [];
	if (hasTimeRegistrationCreateAllPermission) {
		const personDropdown = {
			type: ELEMENT_TYPE.DROPDOWN_PERSON,
			customWidth: 272,
			selectedPerson: selectedPersonId,
			callback: handlePersonChange,
			cy: 'timesheets-select-person',
			userpilot: 'timesheets-select-person',
		};
		leftContent.push(personDropdown);
	}

	const datePickerElement = showCalendar
		? {
				type: ELEMENT_TYPE.SKYLINE_DATE_DISPLAY_WITH_BUTTONS,
				value: formatDate(currentViewingDate.clone() as unknown as Date, {
					year: 'numeric',
					month: 'long',
				}),
				onBackButton: () => adjacentPeriod(false),
				onForwardButton: () => adjacentPeriod(true),
		  }
		: {
				type: ELEMENT_TYPE.SKYLINE_DATE_PICKER_WITH_BUTTONS,
				value: currentViewingDate.clone().format('yyyy-MM-DD'),
				onDatePickerChange: dateString => handleDateRange(moment(dateString)),
				onBackButton: () => adjacentPeriod(false),
				onForwardButton: () => adjacentPeriod(true),
		  };

	const dateTodayButton = {
		type: ELEMENT_TYPE.SKYLINE_BUTTON,
		text: formatMessage({id: 'common.today'}),
		emphasis: EMPHASIS.MONO,
		callback: () => goToToday(),
	};

	leftContent.push(datePickerElement);
	leftContent.push(dateTodayButton);

	const element = {
		type: ELEMENT_TYPE.SEARCH_LAZY,
		onChange: setSearchString,
		value: searchString,
		cy: 'timesheets-table-search',
		placeholder: 'Search',
		alwaysExpanded: searchString !== '',
		useDesignSystemButton: true,
	};

	rightContent.push(element);

	const content = {
		type: ELEMENT_TYPE.CSV,
		callback: handleCsvDownload,
	};
	rightContent.push(content);

	const hideShow = {
		type: ELEMENT_TYPE.HIDE_AND_SHOW,
		cy: 'calendar-toggle',
		userpilot: 'timesheets-toggle-calender',
		showId: 'show-calendar',
		hideId: 'hide-calendar',
		hideText: formatMessage({id: 'timesheet.hide_calendar'}),
		showText: formatMessage({id: 'timesheet.show_calendar'}),
		isShowing: !showCalendar,
		callback: toggleCalendar,
	};
	rightContent.push(hideShow);

	if (showAddTimeRegButton) {
		const addTime = {
			id: 'add-time-entry-button',
			type: ELEMENT_TYPE.SKYLINE_BUTTON,
			emphasis: EMPHASIS.MONO,
			text: formatMessage({id: 'time_approval.add_timesheet_entry'}),
			callback: () => {
				showModal({
					type: MODAL_TYPE.CREATE_TIME_REGISTRATION,
					personId: selectedPerson?.id,
					personLabel: selectedPerson?.fullName,
					initialState: {date: currentViewingDate.toDate()},
				});
			},
			style: BUTTON_STYLE.OUTLINE_THICK,
			color: BUTTON_COLOR.MEDIUMGREY,
			cy: 'new-ui-scoping-custom-button-container',
			dataCy: 'overview-time-add-time-reg',
		};
		rightContent.push(addTime);
	}

	const usingTimeApproval = isUsingTimeApproval(useTimeApproval);
	const usingTimeOffApproval = isUsingTimeOffApproval(useTimeOffApproval);
	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;
	const lockedDate = getLockedDate(company, selectedPerson);

	const companyLockedDate = useMemo(() => getCompanyLockedDate(viewer.company), []);

	const approvalButtons = {
		type: ELEMENT_TYPE.TIMESHEET_APPROVAL_BUTTONS,
		selectedPerson,
		nonViewerPersonSelected: actualPersonId !== selectedPersonId,
		usingTimeApproval,
		usingTimeOffApproval,
		isWeekView,
		currentViewingDate,
		lockedDate,
		companyLockedDate,
		isFullyLocked,
		lastWorkingDayInWeek,
		periodTimeApprovalStatus: periodTimeApprovalStatus,
		hasProjectTimeRegsInWeek,
	};
	rightContent.push(approvalButtons);

	return <HeaderBar leftContent={leftContent} rightContent={rightContent} withSkylineStyle />;
};
